import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchInvoice } from '../../api/invoices';
import { PageHeader, StatusBanner, panelClassName, secondaryButtonClassName } from '../../components/ui';

function InvoicePrintPage({ token }) {
  const { id: invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [status, setStatus] = useState({ type: 'loading', message: '' });

  useEffect(() => {
    let mounted = true;

    fetchInvoice(token, invoiceId)
      .then((payload) => {
        if (!mounted) return;
        setInvoice(payload.invoice);
        setStatus({ type: 'ready', message: '' });
      })
      .catch((error) => {
        if (!mounted) return;
        setStatus({ type: 'error', message: error.message });
      });

    return () => {
      mounted = false;
    };
  }, [token, invoiceId]);

  if (status.type === 'loading') {
    return <p className="text-sm text-slate-500">Loading invoice...</p>;
  }

  if (!invoice) {
    return (
      <StatusBanner
        status={{
          type: 'error',
          message: status.message || 'Invoice not found.',
        }}
        setStatus={setStatus}
      />
    );
  }

  return (
    <section className="grid gap-5">
      <PageHeader
        title={`Invoice ${invoice.invoiceNumber}`}
        subtitle="Printable invoice preview (Phase 1 HTML view)."
        actions={
          <>
            <Link to="/admin/invoices" className={secondaryButtonClassName}>
              Back to list
            </Link>
            <button type="button" className={secondaryButtonClassName} onClick={() => window.print()}>
              Print
            </button>
          </>
        }
      />

      <article className={`${panelClassName} mx-auto w-full max-w-3xl p-8 print:border-0 print:shadow-none`}>
        <header className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Seller CRM</p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-900">{invoice.invoiceNumber}</h1>
          </div>
          <div className="text-right text-sm text-slate-600">
            <p>Status: {invoice.status}</p>
            <p>Issued: {new Date(invoice.issuedAt).toLocaleDateString()}</p>
          </div>
        </header>

        <div className="grid gap-6 py-6 md:grid-cols-2">
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bill to</h2>
            <p className="mt-2 font-medium text-slate-900">
              {invoice.order?.buyerName || 'Customer'}
            </p>
            <p className="text-sm text-slate-600">
              Amazon order: {invoice.order?.amazonOrderId || invoice.orderId || '—'}
            </p>
            <p className="text-sm text-slate-600">
              Marketplace: {invoice.order?.marketplace || '—'}
            </p>
          </div>
          <div className="md:text-right">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Amount due</h2>
            <p className="mt-2 text-3xl font-semibold text-slate-900">
              {Number(invoice.amount).toFixed(2)}
            </p>
          </div>
        </div>

        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-y border-slate-200 bg-slate-50">
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-200">
              <td className="px-3 py-3">Order fulfillment services</td>
              <td className="px-3 py-3 text-right">{Number(invoice.amount).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </article>
    </section>
  );
}

export default InvoicePrintPage;
